import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import {
  getClientCatalogOptions,
  getClientImportOptions,
  getClientOptions,
  getClientsSummaryOptions,
  listClientImportRowsInfiniteOptions,
  listClientsInfiniteOptions,
  nextCursorParam,
  POLLING,
  STALE,
  type ClientImportBatch,
  type ClientImportRowList,
  type ClientList,
  type GetClientData,
} from '@/shared/api'
import { clientImportFailed } from '../model/import'
import { clientSearchParams, type ClientSearch } from '../model/search'

type ClientId = GetClientData['path']['clientId']

/**
 * Карточка гостя. ОДНА на два экрана — и в брони, и в разделе «Гости».
 *
 * Бронь несёт только `clientId`, а карточку читает отсюда: копия профиля внутри брони неизбежно
 * разошлась бы с разделом, а кеш запроса делает второй экран бесплатным — открыл гостя в списке,
 * открыл его же бронь, данные те же.
 */
export function clientQuery(clientId: ClientId) {
  return queryOptions({
    ...getClientOptions({ path: { clientId } }),
    staleTime: STALE.clientDetail,
  })
}

/**
 * Справочник для ФОРМ, и только для них: виды каналов, которые менеджер заводит руками. Для показа
 * он не нужен — подписи приходят рядом со значениями (`kindLabel`), иначе новый вид канала, которого
 * в каталоге ещё нет, стал бы безымянным ровно там, где сервер прислал его подпись.
 */
export function clientCatalogQuery() {
  return queryOptions({
    ...getClientCatalogOptions(),
    staleTime: STALE.reference,
  })
}

/**
 * Сводка по базе гостей — ВЕСЬ экран одним запросом.
 *
 * Отдельных ручек под блоки нет намеренно: все пять агрегатов считаются по одной и той же базе, и
 * пятью запросами они разъехались бы между собой сильнее, чем расходятся сейчас внутри одного
 * (сервер честно предупреждает, что это не мгновенный снимок, — см. `generatedAt`).
 *
 * `STALE.clients`, а не `live`: числа по базе в четырнадцать тысяч человек не меняются посекундно,
 * и контракт прямо разрешает кешировать их на гейтвее.
 */
export function clientSummaryQuery() {
  return queryOptions({
    ...getClientsSummaryOptions(),
    staleTime: STALE.clients,
  })
}

/**
 * База гостей компании — КУРСОРНАЯ подгрузка (§4).
 *
 * Номеров страниц нет и быть не может: курсор непрозрачен, «перейти на страницу 5» и «показать
 * последнюю» невыразимы. Наружу это выходит одной кнопкой «Показать ещё».
 *
 * ⚠️ При недобранном номере запрос НЕ уходит вовсе (`enabled`). Сервер ответил бы на него пустым
 * списком без ошибки, а это неотличимо от «такого гостя нет» — см. `clientSearch`.
 *
 * `refetchInterval` сюда НЕ добавлять: на infinite-запросе поллинг перезапрашивает КАЖДУЮ уже
 * загруженную страницу (см. HANDOFF, «Поллинг на infinite-запросе»). База гостей — экран для
 * просмотра, ей хватает `refetchOnWindowFocus`.
 */
export function clientsQuery(search: ClientSearch) {
  return infiniteQueryOptions({
    ...listClientsInfiniteOptions({ query: clientSearchParams(search) }),
    initialPageParam: {},
    // Конец списка — курсор пустой ИЛИ отсутствующий; почему это не `??` — в `nextCursorParam`.
    getNextPageParam: (lastPage: ClientList) => nextCursorParam(lastPage.nextCursor),
    staleTime: STALE.clients,
    enabled: search.kind !== 'phonePartial',
  })
}

/**
 * Состояние загрузки базы из файла — и ОПРОС при применении.
 *
 * ⚠️ Ради этого опроса ручка и существует. `commitClientImport` возвращается СРАЗУ и применяет
 * фоном — минутами, — поэтому ход виден только отсюда: счётчики записи сервер обновляет раз в
 * 200 строк.
 *
 * Интервал живёт В ЗАПРОСЕ, а не в экране, и включается только пока применение ИДЁТ — то есть на
 * `committing` и без причины сбоя (правило целиком — в `clientImportFailed`). У разобранного
 * отчёта меняться нечему, а у решённого — тем более. Уйдя с экрана и вернувшись (или нажав F5),
 * менеджер попадает в тот же опрос — состояние держит адрес, а не память компонента.
 *
 * `STALE.live` (то есть ноль) намеренно: это ЖИВОЕ состояние. Свежесть в минуту показала бы
 * «применяется» уже законченной загрузке — ровно на том экране, который открывают, чтобы узнать,
 * закончилось ли.
 */
export function clientImportQuery(importId: string) {
  return queryOptions({
    ...getClientImportOptions({ path: { importId } }),
    staleTime: STALE.live,
    refetchInterval: (query) => {
      const batch = query.state.data as ClientImportBatch | undefined
      if (!batch) return false
      // «Идёт» = применяется И не сорвалось. Почему статуса одного мало — в `clientImportFailed`;
      // там же это правило и живёт целиком, чтобы опрос и экран не разошлись в ответе.
      return batch.status === 'committing' && !clientImportFailed(batch) ? POLLING.clientImport : false
    },
  })
}

/** Сколько строк разбора читаем за раз. Контракт: по умолчанию 500, максимум 5000. */
const IMPORT_ROWS_PAGE = 200

/**
 * Строки загрузки — разбор отсеянных и упавших.
 *
 * ⚠️ Постраничность здесь OFFSET/LIMIT, а не курсор, как у базы гостей: строки файла неподвижны и
 * пронумерованы, между запросами не перетасовываются. Смещение следующей страницы — это ровно число
 * уже прочитанных строк, а конец списка виден по `total` под тем же фильтром (без него подгрузка не
 * знала бы, когда остановиться, — в отличие от курсора, который сам говорит «больше нет»).
 */
export function clientImportRowsQuery(importId: string, status: string) {
  return infiniteQueryOptions({
    ...listClientImportRowsInfiniteOptions({
      path: { importId },
      query: { status, limit: IMPORT_ROWS_PAGE },
    }),
    initialPageParam: 0,
    getNextPageParam: (last: ClientImportRowList, pages: ReadonlyArray<ClientImportRowList>) => {
      const loaded = pages.reduce((sum, page) => sum + page.rows.length, 0)
      // `total` — счёт ПОД ФИЛЬТРОМ, поэтому сравнивать с ним можно напрямую. Пустая страница тоже
      // останавливает подгрузку: иначе расхождение `total` с выдачей дало бы вечный запрос.
      return loaded < last.total && last.rows.length > 0 ? loaded : undefined
    },
    staleTime: STALE.clients,
  })
}
